import { Button, Divider, Typography } from '@mui/material'
import { styled } from '@mui/material/styles'
import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import T from 'prop-types'
import * as yup from 'yup'
import logger from 'logger'
import {
  AGENDA_STATE_CLOSED,
  AGENDA_STATE_OPEN,
  AGENDA_TASKS,
  AGENDA_TASK_CREATION,
} from 'lib/agenda'

import TaskDatesRow from './TaskDatesRow'

const GridWrapper = styled('div')(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: '1fr 1fr 2fr',
  columnGap: theme.spacing(3),
  paddingTop: theme.spacing(3),
  paddingBottom: theme.spacing(3),
}))

export default function SessionsPlanning({
  session,
  updateDate,
  handleValidation,
  handleConfirm,
  handleBeforeAssignmentConfirm,
  assignment,
  readOnly,
  contentIsReadOnly = false,
}) {
  const { t } = useTranslation()

  const [sessionState, setSessionState] = useState(session)

  const [datesErrors, setDatesErrors] = useState([])

  useEffect(() => {
    setSessionState(session)
  }, [session])

  const preparation = useMemo(
    () => session.agenda.sessionCreation.state === AGENDA_STATE_OPEN,
    [session.agenda.sessionCreation.state]
  )

  useEffect(() => {
    try {
      const errors = handleValidation(sessionState)

      if (errors.length) {
        setDatesErrors(errors)
      }
    } catch (e) {
      logger.error(Object.entries(e))
    }
  }, [handleValidation, sessionState, setDatesErrors, t])

  const isValid = useMemo(
    () => handleValidation(sessionState).length === 0,
    [handleValidation, sessionState]
  )

  const canConfirmDate = useMemo(() => {
    return (
      (preparation && !sessionState.planningAcceptedAt) ||
      (sessionState.confirmationAcceptedAt && !sessionState.assignmentStartedAt)
    )
  }, [preparation, sessionState])

  const handleDateChange = async (newDate, id) => {
    setDatesErrors([])
    const [task, attribute] = id.split('.')

    setSessionState((s) => ({
      ...s,
      agenda: {
        ...s.agenda,
        [task]: { ...s.agenda[task], [attribute]: newDate },
      },
    }))
    if (yup.date().isValidSync(newDate)) {
      session.agenda.sessionCreation.state !== AGENDA_STATE_CLOSED &&
        (await updateDate(
          sessionState.sessionId,
          sessionState.agenda[task].sessionAgendaId,
          attribute.replace('sAt', ''),
          newDate
        ))
    }
  }

  const handleLocalConfirm = () => {
    preparation
      ? handleConfirm()
      : handleBeforeAssignmentConfirm(session.sessionId, {
          publicationStart: sessionState.agenda.publication.startsAt,
          publicationEnd: sessionState.agenda.publication.endsAt,
          assignmentStart: sessionState.agenda.assignment.startsAt,
          assignmentEnd: sessionState.agenda.assignment.endsAt,
        })
  }

  return (
    <div data-testid="session-planning">
      <GridWrapper>
        <div>
          <Typography variant="body2" color="textSecondary">
            <strong>{t('sessions.sessionId')}</strong>
          </Typography>
          <Typography variant="body2" color="textPrimary">
            <strong>{sessionState.sessionId}</strong>
          </Typography>
        </div>
        <div>
          <Typography variant="body2" color="textSecondary">
            <strong>{t('sessions.planning.status')}</strong>
          </Typography>
          <Typography
            variant="body2"
            color={preparation ? 'text.success' : 'error'}
          >
            <strong>
              {t(
                `sessions.state.sessionCreation.${sessionState.agenda.sessionCreation.state}`
              )}
            </strong>
          </Typography>
        </div>
        <div style={{ textAlign: 'right' }}>
          <Button
            data-testid="button-confirm"
            color="primary"
            variant="contained"
            disabled={
              !isValid ||
              assignment ||
              readOnly ||
              contentIsReadOnly ||
              !canConfirmDate
            }
            onClick={handleLocalConfirm}
          >
            {t('sessions.planning.confirmButton')}
          </Button>
        </div>
      </GridWrapper>
      <Divider light />
      {AGENDA_TASKS.map((task) => {
        return (
          <TaskDatesRow
            key={task}
            agenda={sessionState.agenda}
            task={sessionState.agenda[task]}
            errors={datesErrors}
            handleDateChange={handleDateChange}
            readOnly={
              assignment ||
              (task === AGENDA_TASK_CREATION &&
                session.agenda.sessionCreation.state === 'closed') ||
              readOnly ||
              contentIsReadOnly
            }
          />
        )
      })}
    </div>
  )
}

SessionsPlanning.propTypes = {
  session: T.object.isRequired,
  updateDate: T.func.isRequired,
  handleValidation: T.func.isRequired,
  handleConfirm: T.func.isRequired,
  handleBeforeAssignmentConfirm: T.func.isRequired,
  assignment: T.bool,
  readOnly: T.bool,
  contentIsReadOnly: T.bool,
}
